import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EnkaHeader } from "@/components/EnkaHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  Tent,
  ChevronLeft,
  Wifi,
  Boxes,
  Monitor,
  KeyRound,
  Printer,
  Smartphone,
  Radio,
  Tv,
  ScanBarcode,
  CheckCircle2,
  Loader2,
  ChevronRight,
  Lock,
  LogIn,
  ClipboardList,
  LogOut,
} from "lucide-react";
import { api, TOKEN_KEY } from "@/lib/api";
import { usePersonnel } from "@/lib/personnelContext";
import { AREAS, CATEGORIES_BY_AREA, PRIORITIES } from "@/lib/constants";
import { toast } from "sonner";

// Per-category icon (used in category cards).
const CAT_ICON = {
  "Internet Connection Issue": Wifi,
  "IT Material Request": Boxes,
  "PC Maintenance": Monitor,
  "Access Control Problem": KeyRound,
  "Printer & Toner Issue": Printer,
  "SIM Card Quota Problem": Smartphone,
  "Radio Range": Radio,
  "TV Channel Connection Issue": Tv,
  "Internet Problem": Wifi,
  "Turnstile Fault": ScanBarcode,
  "TV Connection Support": Tv,
};

const AREA_ICON = { Site: Building2, Camp: Tent };

const emptyForm = {
  name: "",
  location: "",
  block: "",
  room: "",
  office: "",
  requester_department: "",
  description: "",
  priority: "Normal",
};

export default function PersonnelFlow() {
  const navigate = useNavigate();
  const { user, signOut } = usePersonnel();
  const [step, setStep] = useState("area"); // area | category | form | success
  const [area, setArea] = useState(null);
  const [category, setCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ticketNo, setTicketNo] = useState(null);
  const [form, setForm] = useState({ ...emptyForm, name: user?.full_name || "" });

  const back = () => {
    if (step === "category") setStep("area");
    else if (step === "form") setStep("category");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const name = form.name.trim();
    const location = form.location.trim();
    const description = form.description.trim();
    if (!name || !location || !description) {
      toast.error("Please fill in name, location and description.");
      return;
    }
    const payload = {
      area,
      category,
      name,
      location,
      description,
      priority: form.priority,
    };
    if (area === "Camp") {
      const block = form.block.trim();
      const room = form.room.trim();
      if (!block || !room) {
        toast.error("Please fill in block and room.");
        return;
      }
      payload.block = block;
      payload.room = room;
    } else if (area === "Site") {
      const office = form.office.trim();
      const requester_department = form.requester_department.trim();
      if (!office || !requester_department) {
        toast.error("Please fill in office and department.");
        return;
      }
      payload.office = office;
      payload.requester_department = requester_department;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/tickets", payload);
      setTicketNo(data.ticket_no);
      setStep("success");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit feedback.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setArea(null);
    setCategory(null);
    setForm({ ...emptyForm, name: user?.full_name || "" });
    setTicketNo(null);
    setStep("area");
  };

  const categories = area ? (CATEGORIES_BY_AREA[area] || []) : [];

  return (
    <div className="min-h-screen flex flex-col" data-testid="personnel-flow">
      <EnkaHeader
        subtitle="Submit your IT-related request or issue."
        rightSlot={
          <div className="flex items-center gap-1.5">
            {user ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate("/my-tickets")}
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/30 bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-2.5 py-1.5 transition-colors"
                  data-testid="personnel-my-tickets-link"
                >
                  <ClipboardList className="h-3 w-3" />
                  <span className="hidden sm:inline">My tickets</span>
                  <span className="sm:hidden">Tickets</span>
                </button>
                <button
                  type="button"
                  onClick={() => { signOut(); toast.success("Signed out"); }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-white/30 bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-2.5 py-1.5 transition-colors"
                  data-testid="personnel-signout-link"
                  aria-label="Sign out"
                >
                  <LogOut className="h-3 w-3" />
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="inline-flex items-center gap-1.5 rounded-md border border-white/30 bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-2.5 py-1.5 transition-colors"
                data-testid="personnel-user-login-link"
              >
                <LogIn className="h-3 w-3" />
                <span className="hidden sm:inline">User Login</span>
                <span className="sm:hidden">Login</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                const dest = localStorage.getItem(TOKEN_KEY) ? "/admin" : "/admin/login";
                navigate(dest);
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-white/30 bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-2.5 py-1.5 transition-colors"
              data-testid="personnel-admin-login-link"
              aria-label="Admin login"
            >
              <Lock className="h-3 w-3" />
              <span className="hidden sm:inline">Admin</span>
            </button>
          </div>
        }
      />

      <main className="flex-1 mx-auto w-full max-w-xl px-5 py-6">
        {step !== "area" && step !== "success" && (
          <button
            type="button"
            onClick={back}
            className="inline-flex items-center text-sm text-[color:var(--enka-blue)] mb-4 hover:underline"
            data-testid="personnel-back-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </button>
        )}

        {step === "area" && (
          <section data-testid="step-area">
            {user && (
              <div
                className="mb-4 rounded-lg border border-[color:var(--enka-blue-50)] bg-[color:var(--enka-blue-50)]/50 px-3 py-2 text-xs text-[color:var(--enka-ink)] flex items-center justify-between gap-2"
                data-testid="personnel-signed-in-banner"
              >
                <div>
                  Signed in as{" "}
                  <span className="font-semibold">{user.full_name}</span>{" "}
                  <span className="text-[color:var(--enka-muted)] font-mono">(@{user.username})</span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/my-tickets")}
                  className="text-[color:var(--enka-blue)] hover:underline text-xs font-medium"
                  data-testid="personnel-signed-in-my-tickets-link"
                >
                  My tickets &rarr;
                </button>
              </div>
            )}
            <h1 className="font-display text-2xl text-[color:var(--enka-ink)]">
              Where is your IT request from?
            </h1>
            <p className="text-sm text-[color:var(--enka-muted)] mt-1">
              Select Site or Camp to continue.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              {AREAS.map((a) => {
                const Icon = AREA_ICON[a];
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() => {
                      setArea(a);
                      setStep("category");
                    }}
                    className="enka-card enka-card-hover text-left p-5 active:scale-[0.99]"
                    data-testid={`area-card-${a.toLowerCase()}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-[color:var(--enka-blue-50)] text-[color:var(--enka-blue)] flex items-center justify-center">
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="font-display text-lg text-[color:var(--enka-ink)]">{a}</div>
                        <div className="text-sm text-[color:var(--enka-muted)]">
                          {a === "Site" ? "Project site IT requests" : "Camp IT requests"}
                        </div>
                      </div>
                      <ChevronRight className="ml-auto h-5 w-5 text-[color:var(--enka-muted)]" />
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === "category" && (
          <section data-testid="step-category">
            <div className="text-xs uppercase tracking-wider text-[color:var(--enka-muted)]">
              Area: <span className="text-[color:var(--enka-blue)] font-semibold">{area}</span>
            </div>
            <h1 className="font-display text-2xl text-[color:var(--enka-ink)] mt-1">
              Choose a category
            </h1>
            <div className="grid grid-cols-1 gap-3 mt-5">
              {categories.map((c) => {
                const Icon = CAT_ICON[c] || Boxes;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      setCategory(c);
                      setStep("form");
                    }}
                    className="enka-card enka-card-hover text-left px-4 py-3 flex items-center gap-3"
                    data-testid={`category-card-${c}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-[color:var(--enka-blue-50)] text-[color:var(--enka-blue)] flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="font-medium text-[color:var(--enka-ink)]">{c}</div>
                    <ChevronRight className="ml-auto h-5 w-5 text-[color:var(--enka-muted)]" />
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {step === "form" && (
          <section data-testid="step-form">
            <div className="text-xs uppercase tracking-wider text-[color:var(--enka-muted)]">
              Area: <span className="text-[color:var(--enka-blue)] font-semibold">{area}</span>
              {" · "}
              Category: <span className="text-[color:var(--enka-blue)] font-semibold">{category}</span>
            </div>
            <h1 className="font-display text-2xl text-[color:var(--enka-ink)] mt-1">
              Request details
            </h1>

            <Card className="mt-5 p-5">
              <form className="space-y-4" onSubmit={submit}>
                <div>
                  <Label htmlFor="name" className="text-[color:var(--enka-ink)]">User / Total User</Label>
                  <Input
                    id="name"
                    data-testid="form-name"
                    placeholder="e.g. Ahmet Yilmaz"
                    value={form.name}
                    onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="location" className="text-[color:var(--enka-ink)]">Location</Label>
                  <Input
                    id="location"
                    data-testid="form-location"
                    placeholder={area === "Camp" ? "e.g. Camp B Building" : "e.g. Site Zone 4 / Workshop"}
                    value={form.location}
                    onChange={(e) => setForm((s) => ({ ...s, location: e.target.value }))}
                    required
                  />
                </div>

                {area === "Camp" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="block" className="text-[color:var(--enka-ink)]">Block</Label>
                      <Input
                        id="block"
                        data-testid="form-block"
                        placeholder="e.g. Block 3"
                        value={form.block}
                        onChange={(e) => setForm((s) => ({ ...s, block: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="room" className="text-[color:var(--enka-ink)]">Room</Label>
                      <Input
                        id="room"
                        data-testid="form-room"
                        placeholder="e.g. Room 214"
                        value={form.room}
                        onChange={(e) => setForm((s) => ({ ...s, room: e.target.value }))}
                        required
                      />
                    </div>
                  </div>
                )}

                {area === "Site" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="office" className="text-[color:var(--enka-ink)]">Office</Label>
                      <Input
                        id="office"
                        data-testid="form-office"
                        placeholder="e.g. Office #3"
                        value={form.office}
                        onChange={(e) => setForm((s) => ({ ...s, office: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="requester_department" className="text-[color:var(--enka-ink)]">
                        Department
                      </Label>
                      <Input
                        id="requester_department"
                        data-testid="form-requester-department"
                        placeholder="e.g. HR, Procurement"
                        value={form.requester_department}
                        onChange={(e) =>
                          setForm((s) => ({ ...s, requester_department: e.target.value }))
                        }
                        required
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-[color:var(--enka-ink)]">Category</Label>
                  <Input value={category} disabled data-testid="form-category-display" />
                </div>

                <div>
                  <Label htmlFor="description" className="text-[color:var(--enka-ink)]">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="form-description"
                    rows={4}
                    placeholder="Describe the issue clearly..."
                    value={form.description}
                    onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <Label className="text-[color:var(--enka-ink)]">Priority</Label>
                  <Select
                    value={form.priority}
                    onValueChange={(v) => setForm((s) => ({ ...s, priority: v }))}
                  >
                    <SelectTrigger data-testid="form-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITIES.map((p) => (
                        <SelectItem key={p} value={p} data-testid={`form-priority-option-${p}`}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-12 bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white text-base"
                  data-testid="form-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </Button>
              </form>
            </Card>
          </section>
        )}

        {step === "success" && (
          <section data-testid="step-success" className="pt-6">
            <div className="enka-card overflow-hidden">
              <div className="bg-[color:var(--enka-blue)] text-white text-center px-6 py-8">
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-white/15 mb-3">
                  <CheckCircle2 className="h-9 w-9" />
                </div>
                <div className="text-sm text-white/80 uppercase tracking-widest font-semibold">
                  Submitted
                </div>
                <h1 className="font-display text-xl mt-1 px-4">
                  Your IT request has been submitted successfully.
                </h1>
              </div>
              <div className="text-center px-6 py-6 bg-white">
                <div className="text-xs uppercase tracking-wider text-[color:var(--enka-muted)] font-semibold">
                  Ticket No
                </div>
                <div
                  className="mt-2 text-3xl font-mono font-bold text-[color:var(--enka-blue)] tracking-wider"
                  data-testid="success-ticket-no"
                >
                  {ticketNo}
                </div>
                <p className="text-sm text-[color:var(--enka-muted)] mt-3 max-w-xs mx-auto">
                  Please keep this number. The IT team will follow up on your request.
                </p>
                <Button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 bg-[color:var(--enka-blue)] hover:bg-[color:var(--enka-blue-600)] text-white"
                  data-testid="success-submit-another-btn"
                >
                  Submit Another
                </Button>
              </div>
            </div>
            <div className="mt-6 text-xs text-[color:var(--enka-muted)] max-w-sm mx-auto text-center">
              <strong>For iPhone:</strong> open in Safari, tap Share, then Add to Home Screen.
              {" "}<strong>For Android:</strong> use the browser menu &rarr; Install app.
            </div>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => navigate("/admin/login")}
                className="text-xs text-[color:var(--enka-muted)] hover:underline"
                data-testid="success-admin-link"
              >
                Admin login &rarr;
              </button>
            </div>
          </section>
        )}
      </main>

      <footer className="text-center text-xs text-[color:var(--enka-muted)] py-4">
        IQ3B9 · IT support
      </footer>
    </div>
  );
}
